import React from 'react';
import { Maximize2 } from 'lucide-react';
import {
  evaluatePrintQuality,
  estimatedPrintInchesAtDpi,
  type ImageResolution,
} from '../services/resolution';

type Props = {
  resolution: ImageResolution | null;
  upscaleEnabled?: boolean;
  onUpscale?: () => void;
  activeUpscaleFactor?: number | null;
  loading?: boolean;
};

export const ResolutionPanel: React.FC<Props> = ({
  resolution,
  upscaleEnabled = true,
  onUpscale,
  activeUpscaleFactor = null,
  loading = false,
}) => {
  if (!resolution) return null;

  const print = evaluatePrintQuality(resolution.width, resolution.height);
  const at300 = estimatedPrintInchesAtDpi(resolution.width, resolution.height, 300);

  return (
    <div className="kin-studio-panel-subtle p-3.5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-teal-700 font-semibold text-xs uppercase tracking-wide">
          <Maximize2 className="w-3.5 h-3.5" />
          Resolution &amp; Print Quality
        </div>
        {activeUpscaleFactor ? (
          <span className="text-[10px] font-bold text-teal-800 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded">
            Viewing {activeUpscaleFactor}×
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Current</div>
          <div className="font-semibold text-slate-800">
            {resolution.width.toLocaleString()} × {resolution.height.toLocaleString()} px
          </div>
          <div className="text-slate-600">{resolution.megapixels} MP</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">
            Estimated print @ 300 DPI
          </div>
          <div className="font-semibold text-slate-800">
            {at300.widthIn} × {at300.heightIn} in
          </div>
          <div className="text-[10px] text-slate-500">Guidance only — DPI is not embedded</div>
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-500">Common print sizes</div>
        <div className="grid grid-cols-2 gap-1.5">
          {print.sizes.map((s) => (
            <div
              key={s.label}
              className="rounded-md border border-slate-200 bg-white/70 px-2 py-1.5 text-[11px]"
            >
              <div className="flex items-center justify-between gap-1">
                <span className="font-semibold text-slate-800">{s.label}</span>
                <span
                  className={
                    s.rating === 'Excellent'
                      ? 'text-teal-700 font-medium'
                      : s.rating === 'Good' || s.rating === 'Acceptable'
                        ? 'text-slate-700'
                        : s.rating === 'Upscale recommended'
                          ? 'text-amber-700'
                          : 'text-rose-700'
                  }
                >
                  {s.rating}
                </span>
              </div>
              <div className="text-slate-500 mt-0.5">
                ~{s.effectiveDpi} DPI
                {s.requiresCrop ? ' · crop' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {upscaleEnabled && onUpscale ? (
        <button
          type="button"
          onClick={onUpscale}
          disabled={loading}
          className="w-full min-h-10 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-xs font-bold transition-colors"
        >
          Prepare for Print
        </button>
      ) : null}
    </div>
  );
};
